import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, XCircle, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiFetch } from "@/lib/api";

interface ActiveCredential {
  id: number;
  name: string;
  company_type: string;
}

interface ImportResult {
  orderId: number;
  success: boolean;
  trackingNumber?: string;
  error?: string;
  skipped?: boolean;
}

interface BulkImportToShippingDialogProps {
  selectedOrderIds: number[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BulkImportToShippingDialog({
  selectedOrderIds,
  isOpen,
  onClose,
  onSuccess,
}: BulkImportToShippingDialogProps) {
  const queryClient = useQueryClient();
  const [credentialId, setCredentialId] = useState<string>("");
  const [numberOfPackages, setNumberOfPackages] = useState(1);
  const [results, setResults] = useState<ImportResult[] | null>(null);

  const { data: credentials = [] } = useQuery<ActiveCredential[]>({
    queryKey: ["shipping-credentials", "active"],
    queryFn: () => apiFetch<ActiveCredential[]>("/api/admin/shipping-credentials/active"),
    enabled: isOpen,
  });

  const importMutation = useMutation({
    mutationFn: (data: { orderIds: number[]; credentialId: number; numberOfPackages: number }) =>
      apiFetch<{ results: ImportResult[] }>("/api/admin/orders/bulk-import-shipping", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      setResults(data.results);
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      queryClient.invalidateQueries({ queryKey: ["shipping-credentials"] });
      onSuccess();
    },
  });

  function handleSubmit() {
    if (!credentialId) return;
    importMutation.mutate({
      orderIds: selectedOrderIds,
      credentialId: parseInt(credentialId),
      numberOfPackages,
    });
  }

  function handleClose() {
    setResults(null);
    setCredentialId("");
    setNumberOfPackages(1);
    importMutation.reset();
    onClose();
  }

  const successResults = results?.filter((r) => r.success) ?? [];
  const failedResults = results?.filter((r) => !r.success && !r.skipped) ?? [];
  const skippedResults = results?.filter((r) => r.skipped) ?? [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Pošalji kuriru</DialogTitle>
          <DialogDescription>
            {results
              ? `Obrađeno ${results.length} narudžbi`
              : `${selectedOrderIds.length} narudžbi za slanje`}
          </DialogDescription>
        </DialogHeader>

        {!results ? (
          <div className="space-y-4">
            {credentials.length === 0 ? (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="size-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Nema konfiguriranih kurirskih službi</p>
                  <p className="text-xs text-amber-700 mt-1">Dodajte kurirsku službu u Podešavanja → Kurirske službe.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Kurirska služba</label>
                  <Select value={credentialId} onValueChange={setCredentialId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Odaberite kurirsku službu" />
                    </SelectTrigger>
                    <SelectContent>
                      {credentials.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Broj paketa po narudžbi</label>
                  <Input
                    type="number"
                    min={1}
                    value={numberOfPackages}
                    onChange={(e) => setNumberOfPackages(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24"
                  />
                </div>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Otkaži
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!credentialId || importMutation.isPending || credentials.length === 0}
              >
                {importMutation.isPending
                  ? "Slanje..."
                  : `Pošalji ${selectedOrderIds.length} narudžbi`}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {successResults.length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                  <CheckCircle2 className="size-3.5" />
                  {successResults.length} uspješno
                </span>
              )}
              {failedResults.length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700">
                  <XCircle className="size-3.5" />
                  {failedResults.length} neuspješno
                </span>
              )}
              {skippedResults.length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                  <SkipForward className="size-3.5" />
                  {skippedResults.length} preskočeno
                </span>
              )}
            </div>

            <div className="max-h-60 overflow-y-auto space-y-1.5">
              {successResults.map((r) => (
                <div key={r.orderId} className="flex items-center justify-between text-sm bg-emerald-50/50 rounded-md px-3 py-1.5">
                  <span>#{r.orderId}</span>
                  <span className="font-mono text-xs text-emerald-700">{r.trackingNumber}</span>
                </div>
              ))}
              {failedResults.map((r) => (
                <div key={r.orderId} className="flex items-center justify-between text-sm bg-red-50/50 rounded-md px-3 py-1.5">
                  <span>#{r.orderId}</span>
                  <span className="text-xs text-red-600 truncate ml-2">{r.error}</span>
                </div>
              ))}
              {skippedResults.map((r) => (
                <div key={r.orderId} className="flex items-center justify-between text-sm bg-gray-50 rounded-md px-3 py-1.5">
                  <span>#{r.orderId}</span>
                  <span className="text-xs text-gray-500">{r.error}</span>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Zatvori</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
